import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useAppSelector } from "@/hooks/hook";
import { GET } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { Skeleton } from "@/components/skeleton/Skeleton";
import { CircularProgress } from "./CircularProgress";

const PointsRewardsSkeleton = () => (
    <div>
        <div className="flex items-center gap-10 flex-wrap justify-center mx-auto max-w-3xl">
            <Skeleton className="rounded-full w-[220px] h-[220px]" />
            <div className="min-w-[220px] flex flex-col items-center gap-3">
                <Skeleton className="h-6 w-56" />
                <Skeleton className="h-4 w-40" />
            </div>
        </div>
        <div className="flex gap-3 mt-8 flex-wrap">
            <Skeleton className="h-12 flex-1 min-w-[160px] rounded-xl" />
            <Skeleton className="h-12 flex-1 min-w-[160px] rounded-xl" />
        </div>
    </div>
);

export const PointsRewardsCard = () => {
    const { refreshToken, business_id } = useAppSelector((store) => store.user);
    const { currentBusiness } = useAppSelector((store) => store.business);
    const effectiveBusinessId = currentBusiness ? currentBusiness : business_id;
    const { getRewardPointsGlobalApi, getRewardPointsBalanceApi, getRewardPointsBreakdownApi, billingDetailsApi } =
        API_ROUTES;

    const [currentPoints, setCurrentPoints] = useState<number>(0);
    const [targetPoints, setTargetPoints] = useState<number>(0);
    const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
    const [breakdown, setBreakdown] = useState<{ label: string; points: number }[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!effectiveBusinessId) return;

        (async () => {
            try {
                setIsLoading(true);
                const [balanceResult, globalResult, breakdownResult] = await Promise.all([
                    GET(`${getRewardPointsBalanceApi}/${effectiveBusinessId}`, refreshToken),
                    GET(`${getRewardPointsGlobalApi}/${effectiveBusinessId}`, refreshToken),
                    GET(`${getRewardPointsBreakdownApi}/${effectiveBusinessId}`, refreshToken),
                ]);

                setCurrentPoints(balanceResult?.data?.record?.total_points ?? 0);
                setTargetPoints(globalResult?.data?.record?.waived_points ?? 0);
                setBreakdown([
                    { label: "Platform Fees", points: breakdownResult?.data?.record?.platform_fee?.percentage ?? 0 },
                    { label: "Alcohol Sales Bonus", points: breakdownResult?.data?.record?.alcohol?.percentage ?? 0 },
                ]);
            } catch (error) {
                console.log("get points rewards error:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [effectiveBusinessId]);

    useEffect(() => {
        if (!effectiveBusinessId) return;

        (async () => {
            try {
                const billingResult = await GET(`${billingDetailsApi}/${effectiveBusinessId}`, refreshToken);
                const rawDate = billingResult?.data?.[0]?.next_billing_date;
                setNextBillingDate(rawDate ? format(new Date(Number(rawDate) * 1000), "MMM d, yyyy") : null);
            } catch (error) {
                console.log("get billing info error:", error);
                setNextBillingDate(null);
            }
        })();
    }, [effectiveBusinessId]);

    const progress = targetPoints > 0 ? (Math.min(currentPoints, targetPoints) / targetPoints) * 100 : 0;
    const pointsToGo = Math.max(targetPoints - currentPoints, 0);
    const message =
        pointsToGo > 0
            ? `You are ${pointsToGo.toLocaleString()} points away from waiving your next subscription fee!`
            : "Your next subscription fee has been waived!";

    return (
        <div className="bg-white rounded-2xl p-8">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Rewards</h3>
                <Link
                    to="/reward"
                    className="bg-black text-white rounded-full py-2 px-6 text-sm"
                >
                    View All
                </Link>
            </div>
            {isLoading ? (
                <PointsRewardsSkeleton />
            ) : (
                <div>
                    <div className="flex items-center gap-10 flex-wrap justify-center mx-auto max-w-3xl">
                        <CircularProgress value={progress} size={220} strokeWidth={16}>
                            <div className="text-center">
                                <p className="text-5xl font-semibold">{currentPoints.toLocaleString()}</p>
                                <p className="text-sm text-gray-400 tracking-wide">PTS</p>
                            </div>
                        </CircularProgress>
                        <div className="min-w-[220px] text-center">
                            <p className="text-xl font-semibold">{message}</p>
                            <p className="text-sm text-gray-400 mt-2">
                                Target: {targetPoints.toLocaleString()} points
                            </p>
                            {nextBillingDate && (
                                <div className="flex items-center gap-2 mt-8 justify-center">
                                    <span className="text-sm text-gray-400">Next Billing Date:</span>
                                    <span className="text-sm font-semibold">{nextBillingDate}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-3 mt-8 flex-wrap">
                        {breakdown.map((item) => (
                            <div
                                key={item.label}
                                className="bg-p-gray rounded-xl px-4 py-3 flex-1 min-w-[160px] flex items-center justify-between"
                            >
                                <p className="text-sm font-semibold">{item.label}:</p>
                                <p className="text-sm font-semibold text-primary-red">
                                    {item.points}%
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
